/**
 * CabinReadinessReport — /programs/kabin-memurlugu/interview/readiness-report
 *
 * F5: Öğrenci Hazırlık Raporu — GET /api/cabin/interview-sim/readiness-report
 * PDF: GET /api/cabin/interview-sim/readiness-report/pdf
 *
 * Tasarım: Vibrant Play + Neumorphism, Framer Motion, WCAG a11y.
 * i18n: readinessReport.* anahtarları LanguageContext'te tanımlı.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  CheckCircle2,
  Star,
  Target,
  AlertCircle,
  Info,
  FileText,
  Download,
  Loader2,
  User,
  TrendingUp,
  RefreshCw,
  Award,
  Camera,
  MessageSquare,
  ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getToken } from '../services/authToken';
import useReducedMotion from '../hooks/useReducedMotion';
import { getScoreColor } from '../lib/interviewScoreBands';
import { useInterviewSimSessions } from '../hooks/cabin/useInterviewSimSessions';
import VerdictBadge from '../components/cabin/interview/VerdictBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Tier icon helper ──────────────────────────────────────────────────────────
function TierIcon({ tier, size = 14 }) {
  if (tier === 'GUCLU')      return <CheckCircle2 size={size} className="text-emerald-500" aria-hidden="true" />;
  if (tier === 'ORTA')       return <Star size={size} className="text-amber-500" aria-hidden="true" />;
  if (tier === 'GELISTIRILECEK') return <Target size={size} className="text-rose-400" aria-hidden="true" />;
  return null;
}

// ── Progress bar with Framer Motion width animation ───────────────────────────
function AnimatedBar({ pct, colorHex, reduced, label }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct ?? 0)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden"
    >
      <motion.div
        className="h-full rounded-full"
        style={{ backgroundColor: colorHex }}
        initial={reduced ? false : { width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </div>
  );
}

// ── Breakdown area row ────────────────────────────────────────────────────────
function BreakdownRow({ areaKey, areaData, lang, reduced, t }) {
  const { score, max, pct, tier, label_tr, label_en } = areaData;
  const label = lang === 'tr' ? (label_tr || areaKey) : (label_en || areaKey);

  // Use getScoreColor(pct) — single source of truth for 3-tier color rule (≥80 high, ≥60 mid, <60 low).
  // areaLabelKey carries the new tier label ("Güçlü"/"Strong", "Geliştirilmeli"/"Needs Improvement", etc.)
  const scoreColor = getScoreColor(pct != null ? pct : 0);

  const tierClass = scoreColor.tailwindText;
  const areaLabel = t(scoreColor.areaLabelKey) || tier;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <TierIcon tier={tier} size={13} />
        <span className="text-xs font-semibold text-gray-700 flex-1 min-w-0 truncate">{label}</span>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold ${tierClass}`}>
            {areaLabel}
          </span>
          <span className="text-xs text-gray-400 tabular-nums min-w-[48px] text-right">
            {score != null ? `${Math.round(score)}/${max}` : '—'}
          </span>
        </div>
      </div>
      <AnimatedBar
        pct={pct}
        colorHex={scoreColor.bar}
        reduced={reduced}
        label={`${label}: ${Math.round(pct ?? 0)}%`}
      />
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────
function ReportSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="h-24 rounded-2xl bg-gray-200" />
      <div className="h-32 rounded-2xl bg-gray-100" />
      <div className="h-64 rounded-2xl bg-gray-100" />
      <div className="h-64 rounded-2xl bg-gray-100" />
    </div>
  );
}

// ── Verdict banner ────────────────────────────────────────────────────────────
function VerdictBanner({ verdict, verdictLabelTr, verdictLabelEn, lang, t, reduced }) {
  const v = verdict || 'KOSULLU';

  // Prototip: amber gradient "Kosullu", emerald "Hazir", rose "Degil"
  // rounded-[20px] p-6 shadow, ikon kutu 58px (prototip spec)
  const cfg = {
    HAZIR: {
      gradient: 'linear-gradient(120deg,#0E7A55,#059669)',
      shadow: 'rgba(14,122,85,.7)',
      Icon: CheckCircle2,
    },
    KOSULLU: {
      gradient: 'linear-gradient(120deg,#F7A823,#F08C18)',
      shadow: 'rgba(240,140,24,.7)',
      Icon: Star,
    },
    DEGIL: {
      gradient: 'linear-gradient(120deg,#E53935,#C62828)',
      shadow: 'rgba(229,57,53,.7)',
      Icon: Target,
    },
  };

  const { gradient, shadow, Icon } = cfg[v] || cfg.KOSULLU;
  const label = lang === 'tr' ? (verdictLabelTr || t(`readinessReport.verdict.${v}.title`)) : (verdictLabelEn || t(`readinessReport.verdict.${v}.title`));
  const sub = t(`readinessReport.verdict.${v}.sub`);

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      role="status"
      aria-live="polite"
      style={{
        background: gradient,
        borderRadius: '20px',
        padding: '24px 26px',
        display: 'flex', alignItems: 'center', gap: '20px',
        color: '#fff',
        boxShadow: `0 16px 34px -12px ${shadow}`,
      }}
    >
      <div style={{
        width: '58px', height: '58px', flexShrink: 0, borderRadius: '16px',
        background: 'rgba(255,255,255,0.22)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={30} color="#fff" aria-hidden="true" />
      </div>
      <div>
        <h2 style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontSize: '26px', fontWeight: '600', margin: 0 }}>
          {label}
        </h2>
        <p style={{ fontSize: '15px', opacity: .95, marginTop: '4px', lineHeight: '1.4' }}>{sub}</p>
      </div>
    </motion.div>
  );
}

// ── Score summary card (CCRI or ARS) ─────────────────────────────────────────
// Bar color is driven by getScoreColor(pct) — same canonical source as BreakdownRow.
// Badge label and colors use new 3-tier pct rule (≥80 high/YEŞİL, ≥60 mid/SARI, <60 low/KIRMIZI).
// Backend band string is intentionally replaced by tier label key for visual consistency.
function ScoreSummaryCard({ title, subtitle, scoreData, t, lang: _lang, reduced, barGradient }) {
  if (!scoreData) return null;
  const { total } = scoreData;
  const scoreColor = getScoreColor(total != null ? total : 0);

  // Badge classes derived from scoreColor (new 3-tier rule)
  const badgeBgBorder =
    scoreColor.tier === 'high'
      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
      : scoreColor.tier === 'mid'
      ? 'bg-amber-50 border-amber-300 text-amber-700'
      : 'bg-red-50 border-red-300 text-red-600';

  const tierLabel = t(scoreColor.labelKey) || scoreColor.tier;

  // Prototip: 52px buyuk rakam, level badge sag ust, bar altta (9px yukseklik)
  return (
    <div style={{
      background: '#fff', border: '1px solid #E6ECF3', borderRadius: '18px',
      padding: '22px', boxShadow: '0 8px 22px rgba(16,40,73,.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{
          fontSize: '12.5px', fontWeight: '800', letterSpacing: '.5px',
          color: '#8593A6', lineHeight: '1.4',
        }}>
          {subtitle}
        </div>
        <span className={`shrink-0 px-3 py-2 rounded-full border-2 text-sm font-black text-center ${badgeBgBorder}`}
          style={{ fontSize: '13px', padding: '8px 14px' }}
          aria-label={`${title}: ${Math.round(total ?? 0)} / 100 — ${tierLabel}`}
        >
          {tierLabel}
        </span>
      </div>
      <div style={{
        fontFamily: "'Fredoka','Nunito',sans-serif",
        fontSize: '52px', fontWeight: '600', lineHeight: '1', marginTop: '14px', color: '#13233F',
      }}>
        {total != null ? Math.round(total) : '—'}
      </div>
      <div style={{ fontSize: '13px', color: '#9AA8BC', marginTop: '4px' }}>
        {t('readinessReport.score.outOf100')}
      </div>
      <div style={{
        height: '9px', borderRadius: '99px', background: '#EDF1F6',
        marginTop: '14px', overflow: 'hidden',
      }}>
        <motion.div
          style={{
            height: '100%', borderRadius: '99px',
            background: barGradient || scoreColor.bar,
            width: `${Math.min(100, Math.max(0, total ?? 0))}%`,
          }}
          initial={reduced ? false : { width: 0 }}
          animate={{ width: `${Math.min(100, Math.max(0, total ?? 0))}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

// ── Risk point item ───────────────────────────────────────────────────────────
// Backend öğrenci endpoint'i yalnızca { constructive_tr, constructive_en } döner;
// flag/severity alanları öğrenci görünümünde sansürlüdür.
// Nötr Clay & Crisp görünüm: Info ikonu, slate palette, severity badge yok.
function RiskItem({ item, lang }) {
  const { constructive_tr, constructive_en } = item;
  const text = lang === 'tr' ? constructive_tr : constructive_en;

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border-2 bg-slate-50 border-slate-200">
      <Info size={16} className="text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-gray-700 leading-snug flex-1">{text}</p>
    </div>
  );
}

// ── Derived area pill (strengths / development) ───────────────────────────────
function DerivedAreaPill({ item, lang, isStrength }) {
  const label = lang === 'tr' ? item.area_label_tr : item.area_label_en;
  const pct = Math.round(item.pct ?? 0);

  if (isStrength) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border-2 border-emerald-200">
        <CheckCircle2 size={14} className="text-emerald-500 shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-emerald-800 flex-1">{label}</span>
        <span className="text-xs font-black text-emerald-600 tabular-nums">{pct}%</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 border-2 border-rose-200">
      <Target size={14} className="text-rose-400 shrink-0" aria-hidden="true" />
      <span className="text-sm font-semibold text-rose-800 flex-1">{label}</span>
      <span className="text-xs font-black text-rose-600 tabular-nums">{pct}%</span>
    </div>
  );
}

// ── Group tile (KP / IN) ──────────────────────────────────────────────────────
function GroupTile({ titleKey, groupData, t }) {
  if (!groupData) return null;
  const { avg_pct, tier, not_applicable } = groupData;

  // not_applicable === true: this group was not evaluated in this interview type
  // (e.g. English group in a TR-language interview). Show a neutral grey badge —
  // never apply red/green tier colours in this case.
  if (not_applicable) {
    return (
      <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 flex flex-col gap-2">
        <p className="text-xs font-bold text-gray-400 tracking-wide">{t(titleKey)}</p>
        <p className="text-sm font-semibold text-gray-400 italic">
          {t('readinessReport.group.notApplicable')}
        </p>
      </div>
    );
  }

  const tierStyle =
    tier === 'GUCLU' ? 'text-emerald-600' :
    tier === 'ORTA'  ? 'text-amber-600'   :
    'text-rose-500';

  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 flex flex-col gap-2">
      <p className="text-xs font-bold text-gray-500 tracking-wide">{t(titleKey)}</p>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-black tabular-nums text-[var(--text-main)]">
          {avg_pct != null ? Math.round(avg_pct) : '—'}%
        </span>
        <span className={`text-sm font-bold mb-0.5 ${tierStyle}`}>
          <TierIcon tier={tier} size={14} />
        </span>
      </div>
      <p className="text-xs text-gray-500">{t('readinessReport.group.avgPct')}</p>
    </div>
  );
}

// ── CV Assessment card ────────────────────────────────────────────────────────
function CVAssessmentCard({ cvData, t }) {
  if (!cvData) return null;
  const { cv_approved, cv_status, cv_career_score } = cvData;

  const statusKey = cv_approved ? 'readinessReport.cv.approved' :
    cv_status ? 'readinessReport.cv.pending' : 'readinessReport.cv.notSubmitted';

  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <FileText size={18} className="text-[var(--brand-primary)]" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-xs font-bold text-gray-500 tracking-wide">
            {t('readinessReport.cv.status')}
          </p>
          <p className={`text-sm font-bold mt-0.5 ${cv_approved ? 'text-emerald-600' : 'text-amber-600'}`}>
            {t(statusKey)}
          </p>
        </div>
        {cv_career_score != null && (
          <div className="text-right">
            <p className="text-xs text-gray-500">{t('readinessReport.cv.careerScore')}</p>
            <p className="text-xl font-black text-[var(--brand-primary)] tabular-nums">{cv_career_score}/10</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function ReportSection({ titleKey, icon: Icon, children, t }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center gap-2 text-sm font-black text-gray-700 tracking-wide">
        {Icon && <Icon size={16} className="text-[var(--brand-primary)]" aria-hidden="true" />}
        {t(titleKey)}
      </h2>
      {children}
    </section>
  );
}

// ── Blob hata body'sini JSON'a parse et ───────────────────────────────────────
// responseType: 'blob' olduğunda hata response'u da blob gelir; text'e çevir, parse et.
async function parseBlobError(err) {
  try {
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json?.detail || json;
    }
  } catch {
    // parse hatası — ignore
  }
  return err?.response?.data?.detail || null;
}

// ── PDF download hook ─────────────────────────────────────────────────────────
function usePDFDownload({ toast, t, lang }) {
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = useCallback(async (sessionId) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const pdfUrl = sessionId
        ? `${API}/cabin/interview-sim/readiness-report/pdf?session_id=${encodeURIComponent(sessionId)}`
        : `${API}/cabin/interview-sim/readiness-report/pdf`;
      const res = await axios.get(
        pdfUrl,
        {
          headers: authHeader(),
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kabin-hazirlik-raporu.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error(t('readinessReport.pdf.error_auth'));
      } else if (status >= 500) {
        toast.error(t('readinessReport.pdf.error_server'));
      } else {
        // Blob body'den mesaj almayı dene
        const detail = await parseBlobError(err);
        const msg =
          (typeof detail === 'object'
            ? (lang === 'tr' ? detail.message_tr : detail.message_en)
            : detail) ||
          t('readinessReport.pdf.error');
        toast.error(msg);
      }
    } finally {
      setDownloading(false);
    }
  }, [downloading, toast, t, lang]);

  return { downloadPDF, downloading };
}

// ── CCRI badge color helper (for session selector CCRI score badge only) ─────
function ccriColor(verdict) {
  if (verdict === 'HAZIR')   return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  if (verdict === 'KOSULLU') return 'bg-amber-50 border-amber-200 text-amber-700';
  return 'bg-rose-50 border-rose-200 text-rose-600';
}

// ── Session selector dropdown ─────────────────────────────────────────────────
function SessionSelector({ sessions, activeId, onSelect, lang, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }).format(new Date(iso));
    } catch { return iso; }
  };

  const active = sessions.find(s => s.session_id === activeId) || sessions[0];

  if (!sessions || sessions.length < 2) return null;

  return (
    <div
      ref={ref}
      className="relative mb-4"
      aria-label={t('readinessReport.sessionSelector.label')}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-2xl text-left hover:border-[var(--brand-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.05)]"
      >
        <FileText size={16} className="text-[var(--brand-primary)] shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-gray-500 block">
            {t('readinessReport.sessionSelector.label')}
          </span>
          {active && (
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              {formatDate(active.scored_at)}
              <VerdictBadge verdict={active.verdict} t={t} iconSize={11} />
              <span className="text-xs text-gray-500 font-mono">CCRI {active.ccri_total ?? '—'}</span>
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            aria-label={t('readinessReport.sessionSelector.label')}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-2xl shadow-lg overflow-hidden"
          >
            {sessions.map((s, idx) => {
              const isActive = s.session_id === (activeId || sessions[0]?.session_id);
              return (
                <li
                  key={s.session_id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onSelect(s.session_id); setOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-[color-mix(in_srgb,var(--brand-primary)_8%,white)] border-l-4 border-[var(--brand-primary)]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <VerdictBadge verdict={s.verdict} t={t} iconSize={12} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-gray-800">
                      {idx === 0
                        ? `${formatDate(s.scored_at)} (${t('readinessReport.sessionSelector.latest')})`
                        : formatDate(s.scored_at)}
                    </span>
                    <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full border ${ccriColor(s.verdict)}`}>
                      CCRI {s.ccri_total ?? '—'}
                    </span>
                  </div>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CabinReadinessReport() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reduced = useReducedMotion();
  const toast = useToast();

  // URL query param: ?session=<id>
  const sessionIdParam = searchParams.get('session') || null;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // AbortController ref — cancels in-flight request when session changes (race condition fix)
  const abortRef = useRef(null);

  const { downloadPDF, downloading } = usePDFDownload({ toast, t, lang });

  // Sessions for the dropdown (only scored v3)
  const { sessions: allSessions } = useInterviewSimSessions();
  const scoredV3Sessions = allSessions.filter(
    s => s.status === 'scored' && s.schema_version === 3
  );

  const fetchReport = useCallback(async (sessionId) => {
    // Abort previous in-flight request before starting a new one
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const url = sessionId
        ? `${API}/cabin/interview-sim/readiness-report?session_id=${encodeURIComponent(sessionId)}`
        : `${API}/cabin/interview-sim/readiness-report`;
      const res = await axios.get(url, {
        headers: authHeader(),
        signal: controller.signal,
      });
      setReport(res.data);
    } catch (err) {
      // Ignore stale (aborted) requests
      if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
      if (err?.response?.status === 404) {
        setReport({ available: false, reason: 'no_scored_session' });
      } else {
        setError(err?.response?.data?.message_tr || err?.response?.data?.detail || 'error');
      }
    } finally {
      // Only clear loading if this request was not aborted
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(sessionIdParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  // Prototip baslik blogu (prototip: "OGRENCI PANELI" eyebrow + h1)
  const ProtoHeader = () => (
    <div style={{ marginBottom: '24px' }}>
      {/* Geri butonu */}
      <button
        type="button"
        onClick={() => navigate('/programs/kabin-memurlugu/interview')}
        className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-xl border-2 border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.06)]"
        aria-label={t('common.back')}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {t('common.back')}
      </button>
      <div style={{ fontSize: '12.5px', fontWeight: '800', letterSpacing: '1px', color: 'var(--brand-primary)', marginBottom: '7px' }}>
        {t('readinessReport.studentPanelEyebrow')}
      </div>
      <h1 style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontWeight: '600', fontSize: '33px', margin: '0', color: '#13233F' }}>
        {t('readinessReport.pageTitle')}
      </h1>
      <p style={{ margin: '7px 0 0', color: '#6B7B92', fontSize: '15px' }}>
        {t('readinessReport.pageSubtitle')}
      </p>
    </div>
  );

  const handleSessionSelect = (sid) => {
    setSearchParams(sid ? { session: sid } : {});
  };

  if (loading) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <ProtoHeader />
        <SessionSelector
          sessions={scoredV3Sessions}
          activeId={sessionIdParam}
          onSelect={handleSessionSelect}
          lang={lang}
          t={t}
        />
        <ReportSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <ProtoHeader />
        <SessionSelector
          sessions={scoredV3Sessions}
          activeId={sessionIdParam}
          onSelect={handleSessionSelect}
          lang={lang}
          t={t}
        />
        <div className="max-w-2xl flex flex-col items-center gap-4 py-8 text-center">
          <AlertCircle size={40} className="text-rose-400" aria-hidden="true" />
          <h2 className="text-lg font-black text-[var(--text-main)]">{t('readinessReport.error.title')}</h2>
          <p className="text-sm text-gray-500">{t('readinessReport.error.sub')}</p>
          <motion.button
            type="button"
            onClick={() => fetchReport(sessionIdParam)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)] hover:brightness-95 transition-all min-h-[44px]"
          >
            <RefreshCw size={16} aria-hidden="true" />
            {t('readinessReport.error.retry')}
          </motion.button>
        </div>
      </div>
    );
  }

  if (!report?.available) {

    // Differentiate messaging by reason:
    // - 'no_scored_session': student has no evaluated sessions at all
    // - 'not_v3_schema': session exists but uses old schema (no new-format report available)
    // - 'pending_review' or any other: sessions exist but are under review → "being evaluated"
    const reason = report?.reason;
    const isNoScored = reason === 'no_scored_session';
    const isNotV3   = reason === 'not_v3_schema';

    const emptyTitleKey = isNoScored
      ? 'readinessReport.empty.no_scored_title'
      : isNotV3
      ? 'readinessReport.empty.not_v3_schema_title'
      : 'readinessReport.empty.pending_title';
    const emptySubKey = isNoScored
      ? 'readinessReport.empty.no_scored_sub'
      : isNotV3
      ? 'readinessReport.empty.not_v3_schema_sub'
      : 'readinessReport.empty.pending_sub';

    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <ProtoHeader />
        <SessionSelector
          sessions={scoredV3Sessions}
          activeId={sessionIdParam}
          onSelect={handleSessionSelect}
          lang={lang}
          t={t}
        />
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className={`w-20 h-20 rounded-2xl border-2 flex items-center justify-center ${
            isNoScored
              ? 'bg-gray-100 border-gray-300'
              : isNotV3
              ? 'bg-blue-50 border-blue-200'
              : 'bg-amber-100 border-amber-300'
          }`}>
            {isNoScored
              ? <FileText size={32} className="text-gray-400" aria-hidden="true" />
              : isNotV3
              ? <Info size={32} className="text-blue-400" aria-hidden="true" />
              : <Loader2 size={32} className="text-amber-500 animate-spin" aria-hidden="true" />
            }
          </div>
          <h2 className="text-lg font-black text-[var(--text-main)]">{t(emptyTitleKey)}</h2>
          <p className="text-sm text-gray-500 max-w-xs leading-relaxed">{t(emptySubKey)}</p>
          {isNoScored && (
            <motion.button
              type="button"
              onClick={() => navigate('/programs/kabin-memurlugu/interview')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)] hover:brightness-95 transition-all min-h-[44px]"
            >
              {t('cabin.interview.history.start_first')}
            </motion.button>
          )}
        </div>
      </div>
    );
  }

  // Full report
  const {
    verdict,
    verdict_label_tr,
    verdict_label_en,
    scores,
    strengths,
    development_areas,
    risk_points,
    kp_group,
    in_group,
    cv_assessment,
    instructor_feedback,
    reinterview,
  } = report;

  // For DEGIL verdict, show strengths before development areas per spec
  const isDEGIL = verdict === 'DEGIL';

  const ccriBreakdownEntries = scores?.ccri?.breakdown ? Object.entries(scores.ccri.breakdown) : [];
  const arsBreakdownEntries  = scores?.ars?.breakdown  ? Object.entries(scores.ars.breakdown)  : [];

  return (
    <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
      <ProtoHeader />
      <SessionSelector
        sessions={scoredV3Sessions}
        activeId={sessionIdParam}
        onSelect={handleSessionSelect}
        lang={lang}
        t={t}
      />
      <div className="flex flex-col gap-6 pb-8">

        {/* ── 1. Verdict banner ─── */}
        <VerdictBanner
          verdict={verdict}
          verdictLabelTr={verdict_label_tr}
          verdictLabelEn={verdict_label_en}
          lang={lang}
          t={t}
          reduced={reduced}
        />

        {/* ── 2. General overview: CCRI + ARS cards ─── */}
        {/* Prototip: 2-kolon grid, CCRI cyan gradient bar, ARS amber gradient bar */}
        <ReportSection titleKey="readinessReport.section.overview" icon={Award} t={t}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <ScoreSummaryCard
              title={t('readinessReport.ccri.title')}
              subtitle={t('readinessReport.ccri.subtitle')}
              scoreData={scores?.ccri}
              t={t}
              lang={lang}
              reduced={reduced}
              barGradient="linear-gradient(90deg,#19C0CE,#1198B6)"
            />
            <ScoreSummaryCard
              title={t('readinessReport.ars.title')}
              subtitle={t('readinessReport.ars.subtitle')}
              scoreData={scores?.ars}
              t={t}
              lang={lang}
              reduced={reduced}
              barGradient="linear-gradient(90deg,#F7A823,#F08C18)"
            />
          </div>
        </ReportSection>

        {/* ── 3. CCRI Breakdown ─── */}
        {ccriBreakdownEntries.length > 0 && (
          <ReportSection titleKey="readinessReport.section.ccriBreakdown" icon={TrendingUp} t={t}>
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
              {ccriBreakdownEntries.map(([key, areaData]) => (
                <BreakdownRow
                  key={key}
                  areaKey={key}
                  areaData={areaData}
                  lang={lang}
                  reduced={reduced}
                  t={t}
                />
              ))}
            </div>
          </ReportSection>
        )}

        {/* ── 4. ARS Breakdown ─── */}
        {arsBreakdownEntries.length > 0 && (
          <ReportSection titleKey="readinessReport.section.arsBreakdown" icon={TrendingUp} t={t}>
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
              {arsBreakdownEntries.map(([key, areaData]) => (
                <BreakdownRow
                  key={key}
                  areaKey={key}
                  areaData={areaData}
                  lang={lang}
                  reduced={reduced}
                  t={t}
                />
              ))}
            </div>
          </ReportSection>
        )}

        {/* ── 5 & 6: Strengths and Development — order depends on verdict ─── */}
        {/* DEGIL: strengths first (positive grounding); HAZIR/KOSULLU: development first */}
        {isDEGIL ? (
          <>
            <StrengthsSection strengths={strengths} lang={lang} t={t} />
            <DevelopmentSection developmentAreas={development_areas} riskPoints={risk_points} lang={lang} t={t} />
          </>
        ) : (
          <>
            <DevelopmentSection developmentAreas={development_areas} riskPoints={risk_points} lang={lang} t={t} />
            <StrengthsSection strengths={strengths} lang={lang} t={t} />
          </>
        )}

        {/* ── 7. CV Assessment ─── */}
        {cv_assessment && (
          <ReportSection titleKey="readinessReport.section.cv" icon={FileText} t={t}>
            <CVAssessmentCard cvData={cv_assessment} t={t} />
          </ReportSection>
        )}

        {/* ── 8. KP & IN groups ─── */}
        {(kp_group || in_group) && (
          <ReportSection titleKey="readinessReport.section.groups" icon={Camera} t={t}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {kp_group && (
                <GroupTile
                  titleKey="readinessReport.group.kp.title"
                  groupData={kp_group}
                  t={t}
                />
              )}
              {in_group && (
                <GroupTile
                  titleKey="readinessReport.group.in.title"
                  groupData={in_group}
                  t={t}
                />
              )}
            </div>
          </ReportSection>
        )}

        {/* ── 9. Instructor Note ─── */}
        {instructor_feedback?.feedback_text && (
          <ReportSection titleKey="readinessReport.section.instructorNote" icon={MessageSquare} t={t}>
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
              <p className="text-sm text-gray-700 leading-relaxed italic">
                "{instructor_feedback.feedback_text}"
              </p>
              {instructor_feedback.scored_by_name && (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <User size={11} aria-hidden="true" />
                  {t('readinessReport.instructorNote.scoredBy').replace('{{name}}', instructor_feedback.scored_by_name)}
                  {instructor_feedback.scored_at && (
                    <span className="ml-1">
                      {t('readinessReport.instructorNote.scoredAt').replace(
                        '{{date}}',
                        new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        }).format(new Date(instructor_feedback.scored_at))
                      )}
                    </span>
                  )}
                </p>
              )}
            </div>
          </ReportSection>
        )}

        {/* ── 10. Re-interview (only when suggested) ─── */}
        {reinterview?.suggested && (
          <ReportSection titleKey="readinessReport.section.reinterview" icon={RefreshCw} t={t}>
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex flex-col gap-2">
              <p className="text-sm font-bold text-amber-800">{t('readinessReport.reinterview.sub')}</p>
              {reinterview.reason_tr && (
                <p className="text-sm text-amber-700 leading-relaxed">
                  {lang === 'tr' ? reinterview.reason_tr : (reinterview.reason_en || reinterview.reason_tr)}
                </p>
              )}
            </div>
          </ReportSection>
        )}

        {/* ── 11. PDF download ─── */}
        <div className="flex flex-col gap-3">
          <motion.button
            type="button"
            onClick={() => downloadPDF(sessionIdParam)}
            disabled={downloading}
            whileHover={reduced ? {} : { scale: 1.02 }}
            whileTap={reduced ? {} : { scale: 0.98 }}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-base shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] disabled:opacity-50 hover:brightness-95 transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
            aria-live="polite"
          >
            {downloading ? (
              <>
                <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                {t('readinessReport.pdf.downloading')}
              </>
            ) : (
              <>
                <Download size={18} aria-hidden="true" />
                {t('readinessReport.pdf.download')}
              </>
            )}
          </motion.button>
        </div>

      </div>
    </div>
  );
}

// ── Sub-components extracted for clarity ─────────────────────────────────────

function StrengthsSection({ strengths, lang, t }) {
  if (!strengths) return null;
  const hasDerived = strengths.derived && strengths.derived.length > 0;
  const hasInstructor = !!strengths.instructor_text;
  if (!hasDerived && !hasInstructor) return null;

  return (
    <ReportSection titleKey="readinessReport.section.strengths" icon={CheckCircle2} t={t}>
      <div className="flex flex-col gap-3">
        {hasDerived && (
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-xs font-bold text-gray-400 tracking-wide">
              {t('readinessReport.strengths.derived.heading')}
            </p>
            {strengths.derived.map((item) => (
              <DerivedAreaPill key={item.area_key} item={item} lang={lang} isStrength={true} />
            ))}
          </div>
        )}
        {hasInstructor && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4">
            <p className="text-xs font-bold text-emerald-600 tracking-wide mb-2">
              {t('readinessReport.strengths.instructor.heading')}
            </p>
            <p className="text-sm text-emerald-800 leading-relaxed">{strengths.instructor_text}</p>
          </div>
        )}
      </div>
    </ReportSection>
  );
}

function DevelopmentSection({ developmentAreas, riskPoints, lang, t }) {
  const hasDerived = developmentAreas?.derived && developmentAreas.derived.length > 0;
  const hasInstructor = !!developmentAreas?.instructor_text;
  const hasRisk = riskPoints && riskPoints.length > 0;
  if (!hasDerived && !hasInstructor && !hasRisk) return null;

  return (
    <ReportSection titleKey="readinessReport.section.development" icon={Target} t={t}>
      <div className="flex flex-col gap-3">
        {hasDerived && (
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-xs font-bold text-gray-400 tracking-wide">
              {t('readinessReport.development.derived.heading')}
            </p>
            {developmentAreas.derived.map((item) => (
              <DerivedAreaPill key={item.area_key} item={item} lang={lang} isStrength={false} />
            ))}
          </div>
        )}
        {hasRisk && (
          <div className="flex flex-col gap-2">
            {riskPoints.map((item, idx) => (
              // key: flag artık öğrenci response'unda gelmiyor; idx yeterli
              <RiskItem key={idx} item={item} lang={lang} />
            ))}
          </div>
        )}
        {hasInstructor && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
            <p className="text-xs font-bold text-amber-700 tracking-wide mb-2">
              {t('readinessReport.development.instructor.heading')}
            </p>
            <p className="text-sm text-amber-800 leading-relaxed">{developmentAreas.instructor_text}</p>
          </div>
        )}
      </div>
    </ReportSection>
  );
}
